import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ContactService } from '../../services/contact.service';
import { ToastService } from '../../shared/toast/toast.service';
import { SecurityService } from '../../services/security.service';

@Component({
  selector: 'app-quote',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './quote.component.html',
  styleUrl: './quote.component.css'
})
export class QuoteComponent {
  // Form fields
  name = '';
  email = '';
  country_code = '+961';
  phone = '';
  propertyType = '';
  budget = '';
  location = '';
  bedrooms = '';
  bathrooms = '';
  neighborhood = '';
  floor = '';
  parkingCovered = '';
  parkingOutdoor = '';
  hasTerrace = false;
  hasGarden = false;
  hasView = false;
  hasElevator = false;
  isFurnished = false;
  generalCondition = '';
  message = '';

  errors = signal<{[key: string]: string}>({});
  isSubmitting = signal(false);
  
  countryCodes = [
    { code: '+961', country: 'Lebanon', flag: '🇱🇧' },
    { code: '+1', country: 'United States', flag: '🇺🇸' },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+971', country: 'UAE', flag: '🇦🇪' },
    { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+20', country: 'Egypt', flag: '🇪🇬' },
    { code: '+962', country: 'Jordan', flag: '🇯🇴' },
    { code: '+90', country: 'Turkey', flag: '🇹🇷' },
    { code: '+34', country: 'Spain', flag: '🇪🇸' },
    { code: '+39', country: 'Italy', flag: '🇮🇹' },
    { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
    { code: '+32', country: 'Belgium', flag: '🇧🇪' },
    { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
    { code: '+46', country: 'Sweden', flag: '🇸🇪' },
    { code: '+47', country: 'Norway', flag: '🇳🇴' },
    { code: '+45', country: 'Denmark', flag: '🇩🇰' },
    { code: '+43', country: 'Austria', flag: '🇦🇹' },
    { code: '+30', country: 'Greece', flag: '🇬🇷' },
    { code: '+351', country: 'Portugal', flag: '🇵🇹' },
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+86', country: 'China', flag: '🇨🇳' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' },
    { code: '+82', country: 'South Korea', flag: '🇰🇷' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
    { code: '+55', country: 'Brazil', flag: '🇧🇷' },
    { code: '+52', country: 'Mexico', flag: '🇲🇽' },
    { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  ];

  constructor(
    private contactService: ContactService,
    private toastService: ToastService,
    private securityService: SecurityService
  ) {}

  validateForm(): boolean {
    const newErrors: {[key: string]: string} = {};

    // Rate limiting
    if (!this.securityService.checkRateLimit('quote_form', 5)) {
      this.toastService.error('Too many requests. Please try again later.');
      return false;
    }

    // Length validation
    if (this.name && !this.securityService.isValidLength(this.name, 255)) {
      newErrors['name'] = 'Name is too long';
    }
    if (this.email && !this.securityService.isValidLength(this.email, 255)) {
      newErrors['email'] = 'Email is too long';
    }
    if (this.message && !this.securityService.isValidLength(this.message, 5000)) {
      newErrors['message'] = 'Message is too long';
    }

    if (!this.name.trim()) {
      newErrors['name'] = 'Name is required';
    }

    if (!this.email.trim()) {
      newErrors['email'] = 'Email is required';
    } else if (!this.securityService.isValidEmail(this.email)) {
      newErrors['email'] = 'Please enter a valid email address';
    }

    if (!this.country_code.trim()) {
      newErrors['country_code'] = 'Country code is required';
    }

    if (!this.phone.trim()) {
      newErrors['phone'] = 'Phone number is required';
    } else if (!this.securityService.isValidPhone(this.phone)) {
      newErrors['phone'] = 'Please enter a valid phone number';
    }

    if (!this.propertyType) {
      newErrors['propertyType'] = 'Property type is required';
    }

    if (!this.budget.trim()) {
      newErrors['budget'] = 'Budget is required';
    }

    if (!this.location.trim()) {
      newErrors['location'] = 'Location is required';
    }

    this.errors.set(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async submitForm() {
    if (this.isSubmitting()) return;
    
    if (!this.validateForm()) {
      this.toastService.show('error', 'Please fill in all required fields');
      return;
    }

    try {
      this.isSubmitting.set(true);
      
      const formattedMessage = `
QUOTE REQUEST

Property Type: ${this.propertyType}
Budget: ${this.budget}
Location: ${this.location}
${this.bedrooms ? `Bedrooms: ${this.bedrooms}` : ''}
${this.bathrooms ? `Bathrooms: ${this.bathrooms}` : ''}
${this.neighborhood ? `Neighborhood: ${this.neighborhood}` : ''}
${this.floor ? `Floor: ${this.floor}` : ''}
${this.generalCondition ? `Condition: ${this.generalCondition}` : ''}

Parking:
${this.parkingCovered ? `Covered: ${this.parkingCovered} spots` : ''}
${this.parkingOutdoor ? `Outdoor: ${this.parkingOutdoor} spots` : ''}

Property Features:
${this.hasElevator ? '✓ Elevator' : ''}
${this.hasTerrace ? '✓ Terrace' : ''}
${this.hasGarden ? '✓ Garden' : ''}
${this.hasView ? '✓ View' : ''}
${this.isFurnished ? '✓ Furnished' : ''}

Additional Details:
${this.message || 'No additional details provided'}
      `.trim();

      await this.contactService.submitContactForm({
        name: this.securityService.sanitizeFormInput(this.name, 255),
        email: this.securityService.sanitizeFormInput(this.email, 255),
        country_code: this.securityService.sanitizeFormInput(this.country_code, 20),
        phone: this.securityService.sanitizeFormInput(this.phone, 50),
        subject: `Quote Request - ${this.securityService.sanitizeFormInput(this.propertyType, 100)}`,
        message: this.securityService.sanitizeFormInput(formattedMessage, 5000)
      });

      this.toastService.show('success', 'Quote request submitted successfully! We\'ll get back to you soon.');
      this.resetForm();
    } catch (error: any) {
      console.error('Error submitting quote request:', error);
      this.toastService.show('error', error.message || 'Failed to submit quote request. Please try again.');
    } finally {
      this.isSubmitting.set(false);
    }
  }

  resetForm() {
    this.name = '';
    this.email = '';
    this.country_code = '';
    this.phone = '';
    this.propertyType = '';
    this.budget = '';
    this.location = '';
    this.bedrooms = '';
    this.bathrooms = '';
    this.neighborhood = '';
    this.floor = '';
    this.parkingCovered = '';
    this.parkingOutdoor = '';
    this.hasTerrace = false;
    this.hasGarden = false;
    this.hasView = false;
    this.hasElevator = false;
    this.isFurnished = false;
    this.generalCondition = '';
    this.message = '';
    this.errors.set({});
  }
}
